import type { LambdaSource, TextureSource } from '@use-gpu/core';
import type { ShaderSource } from '@use-gpu/shader';

import { useMemo, useOne } from '@use-gpu/live';

import { getTextureSampleType, getTextureArrayType, getTypedArraysBitCount } from '@use-gpu/core';
import { castTo, chainTo } from '@use-gpu/shader/wgsl';
import { getShader } from '../hooks/useShader';
import { getTextureAccess, getTextureUVToXY } from '../hooks/useTextureAccess';
import { getLambdaSource } from '../hooks/useLambdaSource';

import { displayFloat } from '@use-gpu/wgsl/display/float.wgsl';
import { displayInt } from '@use-gpu/wgsl/display/int.wgsl';

import { displayAlpha } from '@use-gpu/wgsl/display/alpha.wgsl';
import { displayDepth } from '@use-gpu/wgsl/display/depth.wgsl';
import { displayLogRGB } from '@use-gpu/wgsl/display/log-rgb.wgsl';
import { displayMotionXY } from '@use-gpu/wgsl/display/motion-xy.wgsl';
import { displayMotionZ } from '@use-gpu/wgsl/display/motion-z.wgsl';
import { displayPicking } from '@use-gpu/wgsl/display/picking.wgsl';
import { displayRGB } from '@use-gpu/wgsl/display/rgb.wgsl';
import { displayStencil } from '@use-gpu/wgsl/display/stencil.wgsl';

import { getMultiViewSample } from '@use-gpu/wgsl/display/multiview.wgsl';

const HINT_SHADERS = {
  'alpha': displayAlpha,
  'depth': displayDepth,
  'float': displayFloat,
  'log/rgb': displayLogRGB,
  'motion/xy': displayMotionXY,
  'motion/z': displayMotionZ,
  'picking': displayPicking,
  'rgb': displayRGB,
  'stencil': displayStencil,
};

export const useMultiViewShader = (textures: (ShaderSource | null | undefined)[], empty?: boolean) =>
  useMemo(() => getMultiViewShader(textures, empty), [textures, empty]);

export const getMultiViewShader = (textures: (ShaderSource | null | undefined)[], empty?: boolean) => {
  const n = textures.length + +!!empty;
  return getShader(getMultiViewSample, [n, ...textures]);
};

export const useDisplayShader = (texture: TextureSource) => useOne(() => getDisplayShader(texture), texture);

export const getDisplayShader = (texture: TextureSource): LambdaSource => {
  const {aspect, format, size, hint} = texture;

  const f = getTextureSampleType(format, aspect);
  const a = getTextureArrayType(format, aspect);
  const h = (HINT_SHADERS as any)[hint as string] ?? HINT_SHADERS[inferTextureHint(texture)];

  const t = getTextureUVToXY(getTextureAccess(texture), size).shader;
  if (f === 'f32') {
    const max = typeof hint === 'number' ? hint : 1;
    const s = chainTo(t, getShader(h ?? displayFloat, [1/max]));
    return getLambdaSource(s, texture);
  }
  else {
    const bits = getTypedArraysBitCount(a) ?? 1;
    const max = bits < 32 ? (1 << bits) - 1 : 0xffffffff;

    const c = aspect === 'stencil-only' ? castTo(t, 'vec4<u32>') : t;
    const s = chainTo(c, getShader(displayInt, [1/max]));
    return getLambdaSource(h ? chainTo(s, h) : s, texture);
  }
};

const inferTextureHint = (texture: TextureSource) => {
  const {format, aspect} = texture;

  if (format?.match(/depth/)) return 'depth';
  if (aspect?.match(/stencil/)) return 'stencil';

  return 'rgb';
};
